import styled from 'styled-components'
import { BodyMedium, BodyTiny, Title1, Title6 } from '../../components/styles/Typography'
import { theme } from '../../theme/theme'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { Grid } from '../../components/Grid'
import { SelectInput, SelectOption } from '../../components/inputs/Select'
import { useState, useEffect } from 'react'
import { TextInput } from '../../components/inputs/Text'
import { OnClickButton } from '../../components/buttons/onclick/OnClickButton'
import { useTesttMutation } from '../../api/ester-b2b-api/private/test/testQueries'
import { SupportedLanguage } from '@estercare/ester-shared'
import { Radios } from '../../components/inputs/Radios'
import { Success } from '../../components/icons'

export const TestEmailTemplates = () => {
  const { mutate: postEmail, isPending, isError, error, isSuccess } = useTesttMutation()
  const [showSuccessIcon, setShowSuccessIcon] = useState(false)

  const templateOptions: SelectOption[] = [
    { value: 'v-lkommen-till-estercares-h-lsoportal', label: 'v-lkommen-till-estercares-h-lsoportal' },
    { value: 'reminder-mail-f-retagstj-nsten', label: 'reminder-mail-f-retagstj-nsten' },
    { value: 'din-screening-r-analyserad', label: 'din-screening-r-analyserad' },
    { value: 'resultat-fr-n-uppf-ljningen', label: 'resultat-fr-n-uppf-ljningen' },
    {
      value: 'aktiverat-konto-utan-action-f-retagstj-nsten-1',
      label: 'aktiverat-konto-utan-action-f-retagstj-nsten-1',
    },
    {
      value: 'aktiverat-konto-utan-h-lsosamtal-f-retagstj-n-1',
      label: 'aktiverat-konto-utan-h-lsosamtal-f-retagstj-n-1',
    },
    { value: 'uppf-ljning-screeningen', label: 'uppf-ljning-screeningen' },
  ]

  const languageOptions: SupportedLanguage[] = [SupportedLanguage.SWEDISH, SupportedLanguage.ENGLISH]

  const [selectOption, setSelectOption] = useState<string>(templateOptions[0]?.value || '')
  const [textOption, setTextOption] = useState<string | null>(null)
  const [language, setLanguage] = useState<SupportedLanguage>(SupportedLanguage.SWEDISH)

  useEffect(() => {
    if (!isSuccess) {
      return
    }

    setShowSuccessIcon(true)
    const timer = setTimeout(() => {
      setShowSuccessIcon(false)
    }, 3000)
    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer)
    }
  }, [isSuccess])

  const onSubmit = () => {
    postEmail({
      templateName: textOption || selectOption,
      language,
    })
  }
  return (
    <ComponentLayout>
      <Title1>Test email templates</Title1>
      <StyledBodyMedium>Email will be sent to your portal-account email</StyledBodyMedium>

      <StyledTitle6>Language</StyledTitle6>
      <Grid $columns={3}>
        <Radios
          defaultValue={language}
          options={languageOptions}
          key={language}
          setChange={setLanguage}
          component="component"
        />
      </Grid>
      <Spacing />
      <StyledTitle6>Choose template</StyledTitle6>
      <Grid $columns={3}>
        <SelectInput
          label="Select a template"
          placeholder="Select an option"
          options={templateOptions}
          value={selectOption}
          onChange={setSelectOption}
          disabled={!!textOption}
        />
        <Wrapper>
          <TextInput
            label="Custom template option"
            value={textOption || ''}
            placeholder="Template name"
            onChange={(e) => setTextOption(e.target.value)}
          />
          <StyledBodyTiny>
            If template name is not listed in dropdown, please enter the template name here. It will override the
            selected template.
          </StyledBodyTiny>
        </Wrapper>
      </Grid>

      <Spacing />
      <OnClickButton
        text="Send Email"
        onClick={onSubmit}
        disabled={selectOption.length < 1 && !textOption}
        loading={isPending}
        icon={showSuccessIcon && <Success size={24} color={theme.color.white} />}
      />
      {isError && (
        <ErrorMessage color={theme.color.red}>
          {error?.message?.includes('Access token')
            ? 'You need to be logged-in in the portal to send an email'
            : error?.message?.includes('404')
              ? 'Email template not found. Please check the template name.'
              : 'Failed to send email. Please try again.'}
        </ErrorMessage>
      )}
    </ComponentLayout>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xsmall}px;
`
const StyledBodyTiny = styled(BodyTiny)`
  color: ${theme.color.grey4};
`

const StyledTitle6 = styled(Title6)`
  margin-top: ${theme.spacing.medium}px;
  margin-bottom: ${theme.spacing.small}px;
  text-transform: uppercase;
`
const StyledBodyMedium = styled(BodyMedium)`
  margin-top: ${theme.spacing.small}px;
  margin-bottom: ${theme.spacing.large}px;
`
const Spacing = styled.div`
  margin-bottom: ${theme.spacing.large}px;
`
const ErrorMessage = styled(BodyMedium)`
  margin-top: ${theme.spacing.medium}px;
  color: ${theme.color.red};
`
